import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getPublicComments,
  postPublicComment,
  editPublicComment,
  deletePublicComment,
} from '../../api/files';
import type { Comment, PublicCommentsResponse } from '../../types';

// ── Guest edit token storage ───────────────────────────────────────────
//
// Tokens are stashed in localStorage keyed by `(shareToken, commentId)` so
// the guest keeps edit/delete capability across reloads until the TTL
// elapses. localStorage access is wrapped in try/catch because private-mode
// browsers and SSR throw on `localStorage`.

interface GuestEditEntry {
  token: string;
  expiresAt: string; // ISO
}

function tokenStorageKey(shareToken: string, commentId: string): string {
  return `bandmate:guest-edit:${shareToken}:${commentId}`;
}

function readGuestEditToken(
  shareToken: string,
  commentId: string,
): GuestEditEntry | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(tokenStorageKey(shareToken, commentId));
    if (!raw) return null;
    const parsed: GuestEditEntry = JSON.parse(raw);
    if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
      window.localStorage.removeItem(tokenStorageKey(shareToken, commentId));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeGuestEditToken(
  shareToken: string,
  commentId: string,
  entry: GuestEditEntry,
): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      tokenStorageKey(shareToken, commentId),
      JSON.stringify(entry),
    );
  } catch {
    /* private mode or quota exceeded — non-fatal */
  }
}

function dropGuestEditToken(shareToken: string, commentId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(tokenStorageKey(shareToken, commentId));
  } catch {
    /* ignore */
  }
}

interface SharedCommentsProps {
  shareToken: string;
  viewerIsBandmate: boolean;
  commentPolicy: {
    bandmateCommentsVisible: boolean;
    publicCommentsEnabled: boolean;
  };
}

// ── CommentCard ──────────────────────────────────────────────────────

function CommentCard({
  comment,
  shareToken,
  viewerIsBandmate,
  onEdit,
  onDelete,
}: {
  comment: Comment;
  shareToken: string;
  viewerIsBandmate: boolean;
  onEdit: (commentId: string, newContent: string) => Promise<void>;
  onDelete: (commentId: string) => Promise<void>;
}) {
  const displayName = comment.user?.name ?? comment.guestName ?? 'Guest';
  const displayAvatar = comment.user?.avatarUrl ?? null;
  const scopeLabel = comment.userId ? 'Bandmate' : 'Guest';

  // Only a guest poster can have a local edit token. Bandmate comments are
  // edited through the authenticated /api/cuts/:id/comments endpoint, not
  // here, so we don't render Edit/Delete for them on the public page.
  const editEntry = useMemo(
    () => (!comment.userId ? readGuestEditToken(shareToken, comment.id) : null),
    [shareToken, comment.id, comment.userId],
  );
  const canManage = !!editEntry;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.content);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEdit = () => {
    setDraft(comment.content);
    setError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft(comment.content);
    setError(null);
  };

  const submitEdit = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === comment.content) {
      cancelEdit();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onEdit(comment.id, trimmed);
      setEditing(false);
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to update comment';
      setError(msg);
      // 410 Gone — server says the window is closed. Drop the local token.
      if (err?.response?.status === 410) {
        dropGuestEditToken(shareToken, comment.id);
      }
    } finally {
      setBusy(false);
    }
  };

  const submitDelete = async () => {
    if (!window.confirm('Delete this comment? This cannot be undone.')) return;
    setBusy(true);
    setError(null);
    try {
      await onDelete(comment.id);
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to delete comment';
      setError(msg);
      if (err?.response?.status === 410) {
        dropGuestEditToken(shareToken, comment.id);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex gap-3 py-3" data-comment-id={comment.id}>
      {/* Avatar */}
      <div className="flex-shrink-0 pt-0.5">
        {displayAvatar ? (
          <img
            src={displayAvatar}
            alt={displayName}
            className="w-8 h-8 rounded-full object-cover ring-1 ring-border"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs text-primary font-medium ring-1 ring-border">
            {displayName.charAt(0).toUpperCase()}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-sm font-semibold text-text">{displayName}</span>
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
              comment.userId
                ? 'bg-primary/15 text-primary'
                : 'bg-muted/20 text-muted'
            }`}
          >
            {scopeLabel}
          </span>
          <span className="text-xs text-muted">
            {new Date(comment.createdAt).toLocaleDateString()}
          </span>
        </div>

        {editing ? (
          <div className="space-y-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              maxLength={5000}
              rows={3}
              className="w-full px-3 py-2 text-sm bg-surface border border-border rounded-lg text-text placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y"
            />
            {error && <p className="text-xs text-red-400">{error}</p>}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={submitEdit}
                disabled={busy || !draft.trim()}
                className="px-3 py-1 bg-primary text-white rounded text-xs font-medium hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                disabled={busy}
                className="px-3 py-1 text-xs text-muted hover:text-text transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-text/80 whitespace-pre-wrap break-words">
              {comment.content}
            </p>
            {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
            {canManage && (
              <div className="flex items-center gap-3 mt-1.5">
                <button
                  type="button"
                  onClick={startEdit}
                  disabled={busy}
                  className="text-xs text-muted hover:text-text transition-colors disabled:opacity-50"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={submitDelete}
                  disabled={busy}
                  className="text-xs text-red-400/80 hover:text-red-400 transition-colors disabled:opacity-50"
                >
                  Delete
                </button>
              </div>
            )}
          </>
        )}

        {/* Replies */}
        {comment.replies && comment.replies.length > 0 && (
          <div className="mt-2 ml-4 pl-3 border-l-2 border-border space-y-1">
            {comment.replies.map((reply) => (
              <CommentCard
                key={reply.id}
                comment={reply}
                shareToken={shareToken}
                viewerIsBandmate={viewerIsBandmate}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── CommentComposer ──────────────────────────────────────────────────

const MAX_CONTENT_LENGTH = 5000;
const MAX_GUEST_NAME_LENGTH = 64;

function CommentComposer({
  shareToken,
  viewerIsBandmate,
  publicCommentsEnabled,
  onCommentAdded,
}: {
  shareToken: string;
  viewerIsBandmate: boolean;
  publicCommentsEnabled: boolean;
  onCommentAdded: (comment: Comment) => void;
}) {
  const [content, setContent] = useState('');
  const [guestName, setGuestName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const canComment = viewerIsBandmate || publicCommentsEnabled;
  const isGuest = !viewerIsBandmate;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = content.trim();
      if (!trimmed) return;

      setSubmitting(true);
      setError(null);

      try {
        const created = await postPublicComment(shareToken, {
          content: trimmed,
          guestName: isGuest ? guestName.trim() : undefined,
        });
        // Persist the edit token if the server returned one (guest posts
        // only). The list will re-render and show Edit/Delete for it.
        if (created.guestEditToken && created.guestEditTokenExpiresAt) {
          writeGuestEditToken(shareToken, created.id, {
            token: created.guestEditToken,
            expiresAt: created.guestEditTokenExpiresAt,
          });
        }
        setContent('');
        setGuestName('');
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
        onCommentAdded(created);
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Failed to post comment');
      } finally {
        setSubmitting(false);
      }
    },
    [content, guestName, isGuest, shareToken, onCommentAdded],
  );

  if (!canComment) {
    return (
      <div className="text-center py-6 px-4 text-sm text-muted">
        Commenting is disabled for this share.
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {isGuest && (
        <div>
          <input
            type="text"
            placeholder="Your name"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            maxLength={MAX_GUEST_NAME_LENGTH}
            required
            className="w-full px-3 py-2 text-sm bg-surface border border-border rounded-lg text-text placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
      )}

      <div className="flex items-center gap-2 text-xs text-muted">
        <span>
          {viewerIsBandmate
            ? 'Commenting as bandmate'
            : 'Posting as guest'}
        </span>
      </div>

      <div>
        <textarea
          placeholder={viewerIsBandmate ? 'Write a comment...' : 'Write a comment as a guest...'}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          maxLength={MAX_CONTENT_LENGTH}
          rows={3}
          required
          className="w-full px-3 py-2 text-sm bg-surface border border-border rounded-lg text-text placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y"
        />
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-muted">
            {content.length}/{MAX_CONTENT_LENGTH}
          </span>
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-400">{error}</p>
      )}

      {success && (
        <p className="text-sm text-green-400">Comment posted!</p>
      )}

      <button
        type="submit"
        disabled={submitting || !content.trim()}
        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
      >
        {submitting ? 'Posting...' : 'Post Comment'}
      </button>
    </form>
  );
}

// ── SharedComments ───────────────────────────────────────────────────

export function SharedComments({
  shareToken,
  viewerIsBandmate,
  commentPolicy,
}: SharedCommentsProps) {
  const [data, setData] = useState<PublicCommentsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchComments = useCallback(async () => {
    try {
      const result = await getPublicComments(shareToken);
      setData(result);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setLoading(false);
    }
  }, [shareToken]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleCommentAdded = useCallback(
    (comment: Comment) => {
      setData((prev) =>
        prev
          ? { ...prev, comments: [comment, ...prev.comments] }
          : { comments: [comment], hasHiddenBandmateComments: false, viewerIsBandmate }
      );
    },
    [viewerIsBandmate],
  );

  const handleEdit = useCallback(
    async (commentId: string, newContent: string) => {
      const entry = readGuestEditToken(shareToken, commentId);
      const updated = await editPublicComment(shareToken, commentId, {
        content: newContent,
        guestEditToken: entry?.token,
      });
      setData((prev) =>
        prev
          ? {
              ...prev,
              comments: prev.comments.map((c) =>
                c.id === commentId ? { ...c, content: updated.content } : c,
              ),
            }
          : prev,
      );
    },
    [shareToken],
  );

  const handleDelete = useCallback(
    async (commentId: string) => {
      const entry = readGuestEditToken(shareToken, commentId);
      await deletePublicComment(shareToken, commentId, {
        guestEditToken: entry?.token,
      });
      dropGuestEditToken(shareToken, commentId);
      setData((prev) =>
        prev
          ? {
              ...prev,
              comments: prev.comments
                .filter((c) => c.id !== commentId)
                .map((c) => ({
                  ...c,
                  replies: (c.replies ?? []).filter((r) => r.id !== commentId),
                })),
            }
          : prev,
      );
    },
    [shareToken],
  );

  const nothingVisible =
    data && data.comments.length === 0 && !data.hasHiddenBandmateComments;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h2 className="text-lg font-bold text-text mb-4">
        Comments {data && `(${data.comments.length})`}
      </h2>

      {/* Composer */}
      <div className="mb-6 p-4 bg-surface/50 rounded-lg border border-border">
        <CommentComposer
          shareToken={shareToken}
          viewerIsBandmate={viewerIsBandmate}
          publicCommentsEnabled={commentPolicy.publicCommentsEnabled}
          onCommentAdded={handleCommentAdded}
        />
      </div>

      {/* Comments list */}
      {loading ? (
        <div className="text-center py-8 text-muted text-sm">Loading comments...</div>
      ) : nothingVisible ? (
        <div className="text-center py-8 text-muted text-sm">
          No comments yet.
        </div>
      ) : (
        <div className="divide-y divide-border">
          {data?.comments.map((comment) => (
            <CommentCard
              key={comment.id}
              comment={comment}
              shareToken={shareToken}
              viewerIsBandmate={viewerIsBandmate}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Hidden bandmate comments message */}
      {data?.hasHiddenBandmateComments && data.comments.length === 0 && (
        <div className="mt-4 p-3 bg-surface/40 rounded-lg border border-border text-center text-sm text-muted">
          Bandmates have left comments — open in the app to see them.
        </div>
      )}
    </div>
  );
}